import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import {
  fetchCheckpointRollout,
  nextSetupAction,
  type CheckpointRolloutRow,
} from "../data/checkpoint-rollout";
import { useReadModel } from "../data/useReadModel";
import { ActiveMaterialBadge } from "./ActiveMaterialInventory";
import { useTokenMintSession } from "./token-mint-session";

// Per-checkpoint rollout readiness (read-only) — the drilldown under the Patrols route cards.
// Needs-attention first. NFC is the primary method, QR the backup (reflected in the readiness chips).
export function CheckpointRollout({
  client,
  accountId,
  canMint = false,
}: {
  client: SupabaseClient;
  accountId: string;
  canMint?: boolean;
}) {
  const session = useTokenMintSession();
  const mintEnabled = canMint && session?.enabled === true;
  const mintGeneration = session?.mintGeneration ?? 0;

  const { status, data, error } = useReadModel<CheckpointRolloutRow[]>(
    () => fetchCheckpointRollout(client, accountId),
    [client, accountId, mintGeneration],
  );

  if (status === "loading" && data === null) {
    return (
      <p className="surface__state" role="status">
        Loading checkpoint readiness…
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load checkpoint readiness."}
      </p>
    );
  }

  // Needs-attention first: anything not "ready", then by blocker count.
  const rows = [...(data ?? [])].sort((left, right) => {
    const leftReady = left.rolloutStatus === "ready" ? 1 : 0;
    const rightReady = right.rolloutStatus === "ready" ? 1 : 0;
    if (leftReady !== rightReady) return leftReady - rightReady;
    return right.blockers.length - left.blockers.length;
  });

  if (rows.length === 0) {
    return (
      <p className="surface__state" role="status">
        No checkpoint readiness yet. Add checkpoints to a route in Setup.
      </p>
    );
  }

  // Fidelity slice 3: ready checkpoints collapse into a proof rollup + compact name chips;
  // FULL cards render only for checkpoints that need attention.
  const readyRows = rows.filter((row) => row.rolloutStatus === "ready");
  const attentionRows = rows.filter((row) => row.rolloutStatus !== "ready");

  return (
    <div className="ckrollout__wrap">
      <p className={`ckrollout__rollup${attentionRows.length === 0 ? " ckrollout__rollup--all" : ""}`} role="status">
        {readyRows.length} of {rows.length} checkpoints ready
      </p>
      {readyRows.length > 0 ? (
        <div className="ckrollout__readychips" aria-label="Ready checkpoints">
          {readyRows.map((row, index) => (
            <span key={row.checkpointId || `${row.checkpointName}-${index}`} className="ckrollout__readychip">
              {row.checkpointName}
            </span>
          ))}
        </div>
      ) : null}
      {attentionRows.length > 0 ? (
    <ul className="ckrollout">
      {attentionRows.map((row, index) => (
        <li key={row.checkpointId || `${row.checkpointName}-${index}`} className="ckrollout__item">
          <div className="ckrollout__head">
            <span className="ckrollout__name">
              {row.checkpointName}
              {row.isRequired ? <span className="ckrollout__req">required</span> : null}
            </span>
            <span className={`ckrollout__status ckrollout__status--${row.rolloutStatus}`}>
              {humanizeLabel(row.rolloutStatus)}
            </span>
          </div>
          <div className="ckrollout__where">
            {[row.routeName, row.siteName].filter(Boolean).join(" · ") || "No route"}
            {row.preferredMethod ? ` · prefers ${humanizeLabel(row.preferredMethod)}` : ""}
          </div>
          <div className="ckrollout__chips">
            {row.usesNfc || row.usesQr ? (
              <span className={chipClass(row.hasCheckpointMaterial)}>Material</span>
            ) : null}
            {row.hasGeofence || row.blockers.includes("missing_geofence_config") ? (
              <span className={chipClass(!row.blockers.includes("missing_geofence_config"))}>Geofence</span>
            ) : null}
          </div>
          <div className="ckrollout__action">Next: {nextSetupAction(row)}</div>
          <div className="ckrollout__inventory">
            <ActiveMaterialBadge row={row} />
          </div>
          {mintEnabled && row.operationalStatus === "active" && (row.usesQr || row.usesNfc) ? (
            <div className="ckrollout__actions">
              {session!.hasSessionKit(row.checkpointId) ? (
                <button
                  type="button"
                  className="builder__navbtn"
                  aria-label={`View kit for ${row.checkpointName}`}
                  onClick={() => session!.openSessionKit(row.checkpointId)}
                >
                  View kit
                </button>
              ) : null}
              <button
                type="button"
                className={row.hasCheckpointMaterial ? "builder__navbtn" : "minter__confirm-go"}
                aria-label={
                  row.hasCheckpointMaterial
                    ? `Replace material for ${row.checkpointName}`
                    : `Create material for ${row.checkpointName}`
                }
                onClick={() =>
                  session!.openCheckpointConfirm({
                    checkpointId: row.checkpointId,
                    checkpointName: row.checkpointName,
                    routeLabel: [row.routeName, row.siteName].filter(Boolean).join(" · "),
                    hasExistingMaterial: row.hasCheckpointMaterial,
                    replace: row.hasCheckpointMaterial,
                  })
                }
              >
                {row.hasCheckpointMaterial ? "Replace material" : "Create material"}
              </button>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
      ) : null}
    </div>
  );
}

function chipClass(ready: boolean): string {
  return ready ? "ckrollout__chip ckrollout__chip--ok" : "ckrollout__chip ckrollout__chip--warn";
}
